// Grids & Gantts — a ZQL editor that HELPS: the query is highlighted as it
// is typed (fields, operators, values, functions, keywords), the caret's
// place in the grammar decides what is offered (a field · the operators
// that field allows · the field's real values, functions and EMPTY · AND /
// OR / ORDER BY), ↑ ↓ Enter Tab pick, and every problem is named in plain
// words at the spot it sits — with a one-click FIX where the fix is obvious
// (close the quote / the bracket, "did you mean duedate", drop the dangling
// AND, quote a value with spaces). When the text parses, the engine itself
// is asked (POST /api/search/count, debounced) and either the count or the
// engine's own message, at its position, is shown. Enter (or the button)
// applies; the last eight applied queries are kept PER ACCOUNT (on the
// server, zebra-search/recent-queries — they follow the person to any
// device) and offered when the box is empty. A list this browser kept
// before that move is copied up once (recentQueries.copyUpOnce). A SAVED FILTER can be pulled into the box — its
// text lands there to be edited, and is applied like anything typed.
//
// The grammar is ./zqlGrammar.js (pure, tested in the engine suite) and
// mirrors modules/database/zql/src/parser.js. The field list and each
// field's operators come from /api/jql/autocompletedata (the engine's own
// registry), values from /api/jql/suggestions, saved filters from
// /api/filters (the list carries no ZQL — GET /api/filters/:id does).
//
// Props: { value, onChange, onApply, onClear, api, ui, S, placeholder, hint,
//          recentKey, countWords (count → the words after ✓), savedFilters,
//          applied (the query in force, for the "applied" tick), disabled }

import { useEffect, useMemo, useRef, useState } from 'react';
import { client } from '@platform/http/api-client';
import { FONT } from './theme';
import { KW, endProblems, lower, quoteIfNeeded, rankValues, tokenize, walk } from './zqlGrammar';
import recentQueries from '@modules/zebra-search/frontend/recentQueries';

const MONO = 'ui-monospace, "Cascadia Mono", "SF Mono", Menlo, Consolas, monospace';
const JOINS = [{ v: 'AND', d: 'both sides must hold' }, { v: 'OR', d: 'either side holds' }, { v: 'ORDER BY', d: 'sort the result' }];
const DATE_FN = [{ v: 'now()', d: 'this moment' }, { v: 'startOfDay()', d: 'today, 00:00 — startOfDay("-7d") a week ago' }, { v: 'endOfDay()', d: 'today, 23:59' }, { v: 'startOfWeek()', d: 'this week' }, { v: 'endOfWeek()', d: 'the end of this week' }, { v: 'startOfMonth()', d: 'this month' }, { v: 'endOfMonth()', d: 'the end of this month' }, { v: 'field("")', d: 'another field on the same work item — duedate < field("Planned End")' }];
const OP_DESC = {
  '=': 'equals', '!=': 'does not equal', 'in': 'is one of (a, b, c)', 'not in': 'is none of', 'is': 'IS EMPTY / IS NOT EMPTY',
  'is not': 'has a value', '~': 'contains the text', '!~': 'does not contain', '<': 'before / less than', '<=': 'on or before', '>': 'after / more than', '>=': 'on or after',
  'was': 'history (not on this data)', 'changed': 'history (not on this data)',
};

// The words for a check that failed without the engine saying why: that is
// the session or the server, not the query, and must not read as if the ZQL
// were wrong.
function checkFailure(status, message) {
  if (status === 401 || status === 403) return 'Your session has expired — sign in again to check this query';
  if (status >= 500) return `The server could not check this query just now (${status}) — the query itself may be fine; edit or Apply to try again`;
  return message || 'The engine refused this query';
}
const errText = (e, fallback) => (e && e.response && e.response.data && e.response.data.error) || (e && e.message) || fallback;

export default function ZqlBox({
  value, onChange, onApply, onClear = null, api, ui, S,
  placeholder = 'ZQL — e.g. status = Open AND duedate < endOfMonth()', hint,
  recentKey = 'grids-zql-recent-v1',
  countWords = (n) => `work item${n === 1 ? '' : 's'} match site-wide`,
  savedFilters = true, applied = '', disabled = false,
}) {
  const taRef = useRef(null);
  const [registry, setRegistry] = useState(new Map());          // lower(name) → { name, operators }
  const [functions, setFunctions] = useState([]);
  const [caret, setCaret] = useState(0);
  const [focus, setFocus] = useState(false);
  const [sel, setSel] = useState(0);
  const [values, setValues] = useState({ key: '', list: [] });   // the field's values for the current partial
  const [check, setCheck] = useState(null);                       // { count } | { error, pos } | 'checking' | null
  const [recent, setRecent] = useState(() => { try { return JSON.parse(localStorage.getItem(recentKey) || '[]'); } catch { return []; } });
  // The account's list for this place; the browser's old list copied up once.
  const recentScope = `grids:${String(recentKey).replace(/[^A-Za-z0-9._:-]/g, '-').slice(0, 110)}`;
  useEffect(() => {
    let on = true;
    recentQueries.copyUpOnce(recentScope, recentKey).catch(() => null)
      .then(() => recentQueries.list(recentScope))
      .then((list) => { if (on) setRecent(list); })
      .catch(() => { /* the server is unreachable — the browser's list stays */ });
    return () => { on = false; };
  }, [recentScope, recentKey]);
  useEffect(() => {
    let on = true;
    api.getJqlAutocompleteData().then((d) => {
      if (!on) return;
      const m = new Map();
      ((d && d.visibleFieldNames) || []).forEach((f) => { if (f && f.value) m.set(lower(f.value), { name: f.value, operators: Array.isArray(f.operators) ? f.operators : [] }); });
      setRegistry(m);
      setFunctions(((d && d.visibleFunctionNames) || []).map((f) => f.value).filter(Boolean));
    }).catch(() => {});
    return () => { on = false; };
  }, [api]);

  const tokens = useMemo(() => tokenize(value), [value]);
  const whole = useMemo(() => walk(tokens, registry), [tokens, registry]);
  const problems = useMemo(() => [...whole.problems, ...endProblems(value, whole)], [whole, value]);
  // The token the caret sits in (an unfinished word) → the partial and the
  // grammar state BEFORE it.
  const at = useMemo(() => {
    const tok = tokens.find((t) => t.type !== 'ws' && caret > t.start && caret <= t.end && (t.type === 'ident' || t.type === 'kw' || t.type === 'string' || t.type === 'number'));
    const w = walk(tokens, registry, tok ? tok.start : caret);
    return { ...w, partial: tok ? tok.value : '', tok };
  }, [tokens, registry, caret]);

  // While a word is still being typed, or the caret sits at the end of an
  // unfinished clause, that is not a problem yet — nothing is said until the
  // caret moves on (Apply still waits for a clean query).
  const shown = useMemo(() => {
    if (!focus) return problems;
    const endAt = value.replace(/\s+$/, '').length;
    return problems.filter((p) => !(at.tok && p.pos >= at.tok.start && p.pos < at.tok.end) && !(p.pos >= endAt && caret >= endAt));
  }, [problems, focus, at.tok, value, caret]);
  // Values for the field at the caret, from the site (debounced).
  useEffect(() => {
    if (!focus || (at.state !== 'value' && at.state !== 'listValue')) return undefined;
    if (!at.field || at.op === 'is') return undefined;
    const key = `${lower(at.field)}|${lower(at.partial)}`;
    if (values.key === key) return undefined;
    const h = setTimeout(() => {
      api.getJqlSuggestions(at.field, at.partial).then((r) => {
        const list = ((r && r.results) || r || []).map((x) => ({ v: x.value != null ? x.value : x.displayName, d: x.displayName && x.displayName !== x.value ? x.displayName : '' })).filter((x) => x.v != null);
        setValues({ key, list });
      }).catch(() => setValues({ key, list: [] }));
    }, 180);
    return () => clearTimeout(h);
  }, [at, focus, api, values.key]);

  // What to offer at the caret.
  const suggestions = useMemo(() => {
    if (!focus) return [];
    const p = lower(at.partial);
    // Ranked by the typed prefix, then alphabetically; with nothing typed the
    // list keeps its own order (the registry's operators, the site's values).
    const rank = (list, key) => (!p ? list : list.filter((x) => lower(x[key]).includes(p)).sort((a, b) => { const ap = lower(a[key]).startsWith(p) ? 0 : 1; const bp = lower(b[key]).startsWith(p) ? 0 : 1; return ap - bp || String(a[key]).localeCompare(String(b[key])); }));
    if (!value.trim() && recent.length) return recent.map((r) => ({ v: r, kind: 'recent', d: 'recent query' }));
    if (at.state === 'field' || at.state === 'orderField') {
      const fields = [...registry.values()].map((f) => ({ v: f.name, kind: 'field', d: f.operators.slice(0, 5).join(' ') }));
      const extra = at.state === 'field' && !at.partial ? [{ v: 'NOT', kind: 'keyword', d: 'negate the next clause' }, { v: '(', kind: 'keyword', d: 'open a group' }] : [];
      return [...rank(fields, 'v'), ...extra].slice(0, 14);
    }
    if (at.state === 'op') {
      const def = registry.get(lower(at.field)) || null;
      const ops = (def && def.operators.length ? def.operators : ['=', '!=', 'in', 'not in', 'is', 'is not', '~', '!~', '<', '<=', '>', '>=']).map((o) => ({ v: o, kind: 'operator', d: OP_DESC[lower(o)] || '' }));
      return rank(ops, 'v').slice(0, 14);
    }
    if (at.state === 'value' || at.state === 'listValue') {
      if (at.op === 'is') return [{ v: 'EMPTY', kind: 'keyword', d: 'no value' }, { v: 'NOT EMPTY', kind: 'keyword', d: 'any value' }].filter((x) => !p || lower(x.v).includes(p));
      const vals = values.list.map((x) => ({ v: x.v, kind: 'value', d: x.d }));
      const fns = /date|created|updated|resolved|due/.test(lower(at.field)) ? DATE_FN.map((f) => ({ ...f, kind: 'function' })) : functions.map((f) => ({ v: `${f}()`, kind: 'function', d: '' }));
      const empties = at.state === 'value' ? [{ v: 'EMPTY', kind: 'keyword', d: 'no value' }] : [];
      // A value is found by what it SAYS as well as what it is: the site
      // matched the typed text against the project's name (or the person's)
      // too, so re-filtering on the key alone threw those answers away.
      return [...rankValues(vals, p), ...rank([...fns, ...empties], 'v')].slice(0, 14);
    }
    if (at.state === 'join') return rank(JOINS.map((j) => ({ ...j, kind: 'keyword' })), 'v');
    if (at.state === 'listSep') return [{ v: ',', kind: 'keyword', d: 'another value' }, { v: ')', kind: 'keyword', d: 'close the list' }];
    if (at.state === 'by') return [{ v: 'BY', kind: 'keyword', d: '' }];
    if (at.state === 'orderDir') return [{ v: 'ASC', kind: 'keyword', d: 'ascending' }, { v: 'DESC', kind: 'keyword', d: 'descending' }];
    return [];
  }, [focus, at, registry, values.list, functions, value, recent]);
  useEffect(() => { setSel(0); }, [suggestions.length, at.partial, at.state]);

  // The engine's verdict, once the text is clean.
  useEffect(() => {
    const q = value.trim();
    if (!q || problems.length) { setCheck(null); return undefined; }
    setCheck('checking');
    const h = setTimeout(async () => {
      try { const r = await client.post('/api/search/count', { jql: q }); setCheck({ count: (r.data && r.data.count) || 0 }); }
      catch (e) { const st = e && e.response && e.response.status; const d = e && e.response && e.response.data; setCheck({ error: (d && (d.details || d.error)) || checkFailure(st, e && e.message), pos: d && typeof d.pos === 'number' ? d.pos : null }); }
    }, 450);
    return () => clearTimeout(h);
  }, [value, problems.length]);

  const setText = (next, caretAt) => {
    onChange(next);
    requestAnimationFrame(() => { const ta = taRef.current; if (!ta) return; const c = caretAt == null ? next.length : caretAt; ta.setSelectionRange(c, c); setCaret(c); ta.focus(); });
  };
  const pick = (s) => {
    if (s.kind === 'recent') { setText(s.v, s.v.length); return; }
    let ins = s.v;
    if (s.kind === 'value') ins = quoteIfNeeded(String(s.v));
    if (s.kind === 'field' && at.op == null && at.state === 'field') ins = quoteIfNeeded(String(s.v));
    const start = at.tok ? at.tok.start : caret; const end = at.tok ? at.tok.end : caret;
    const before = value.slice(0, start); const after = value.slice(end);
    const next = `${before}${ins} ${after.replace(/^\s+/, '')}`;
    setText(next, before.length + ins.length + 1);
  };
  const apply = () => {
    const q = value.trim();
    if (problems.length || disabled) return;
    if (q) { setRecent([q, ...recent.filter((r) => r !== q)].slice(0, 8)); recentQueries.record(recentScope, q).then(setRecent).catch(() => {}); }
    onApply(q);
    setFocus(false); if (taRef.current) taRef.current.blur();
  };
  const onKey = (e) => {
    if (suggestions.length && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) { e.preventDefault(); setSel((i) => (i + (e.key === 'ArrowDown' ? 1 : -1) + suggestions.length) % suggestions.length); return; }
    // ENTER TAKES THE HIGHLIGHTED OFFER whenever there is one worth taking:
    // the reader moved to it, it is a recent query, the query cannot run yet
    // (an operator, a value, a closing bracket is still owed), or a word is
    // half typed and the offer completes it. Only a clean, finished query
    // runs on Enter — so a purple AND / = / ) is never silently ignored.
    const cur = suggestions[sel];
    const completes = cur && at.tok && lower(String(cur.v)) !== lower(at.partial);
    if (cur && (e.key === 'Tab' || (e.key === 'Enter' && !e.shiftKey && (sel > 0 || cur.kind === 'recent' || problems.length > 0 || completes)))) { e.preventDefault(); pick(cur); return; }
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); apply(); return; }
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setFocus(false); if (taRef.current) taRef.current.blur(); }
  };

  // ---- saved filters, pulled into the box ----------------------------------------
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState(null);                   // null | [] | { error }
  const [filterQ, setFilterQ] = useState('');
  const [pull, setPull] = useState(null);                         // null | 'busy' | { ok: name } | { error }
  const filtersRef = useRef(null);
  useEffect(() => {
    if (!filtersOpen) return undefined;
    const away = (e) => { if (filtersRef.current && !filtersRef.current.contains(e.target)) setFiltersOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') { e.stopPropagation(); setFiltersOpen(false); } };
    document.addEventListener('mousedown', away);
    window.addEventListener('keydown', esc, true);
    return () => { document.removeEventListener('mousedown', away); window.removeEventListener('keydown', esc, true); };
  }, [filtersOpen]);
  const openFilters = () => {
    setFiltersOpen((o) => !o);
    if (filters && !filters.error) return;
    setFilters(null);
    client.get('/api/filters').then((r) => {
      const list = Array.isArray(r.data) ? r.data : ((r.data && (r.data.filters || r.data.values)) || []);
      setFilters(list.map((f) => ({ id: f.id, name: String(f.name || ''), description: String(f.description || ''), owner: (f.owner && f.owner.displayName) || '', starred: !!f.starredByMe }))
        .filter((f) => f.id != null && f.name)
        .sort((a, b) => (b.starred ? 1 : 0) - (a.starred ? 1 : 0) || a.name.localeCompare(b.name)));
    }).catch((e) => setFilters({ error: errText(e, 'The saved filters could not be read.') }));
  };
  const pullFilter = async (f) => {
    setFiltersOpen(false); setPull('busy');
    try {
      const r = await client.get(`/api/filters/${encodeURIComponent(f.id)}`);
      const d = r.data || {};
      const text = String(d.jql || d.zql || d.sourceJql || '').trim();
      if (!text) throw new Error(`"${f.name}" has no ZQL.`);
      setPull({ ok: f.name });
      setText(text, text.length);
    } catch (e) {
      setPull({ error: errText(e, 'The filter could not be read.') });
    }
  };
  const shownFilters = useMemo(() => {
    if (!Array.isArray(filters)) return [];
    const q = lower(filterQ.trim());
    return (q ? filters.filter((f) => lower(`${f.name} ${f.description} ${f.owner}`).includes(q)) : filters).slice(0, 60);
  }, [filters, filterQ]);

  // ---- drawing ---------------------------------------------------------------------
  // The highlighted mirror behind the transparent textarea.
  const errorRanges = useMemo(() => { const r = shown.map((p) => [p.pos, p.pos + p.len]); if (check && check.error && check.pos != null) r.push([check.pos, check.pos + 1]); return r; }, [shown, check]);
  const colorOf = (t, i) => {
    if (t.type === 'string') return ui.green;
    if (t.type === 'number') return ui.amber;
    if (t.type === 'kw' || t.type === 'op') return ui.purple;
    if (t.type === 'ident') { const next = tokens.slice(i + 1).find((x) => x.type !== 'ws'); if (next && next.type === 'lparen' && !KW.has(lower(t.value))) return ui.teal; return registry.size && registry.has(lower(t.value)) ? ui.blue : ui.fg; }
    if (t.type === 'func') return ui.teal;
    if (t.type === 'bad') return ui.red;
    return ui.fg;
  };
  const inError = (t) => errorRanges.some(([a, b]) => t.start < b && t.end > a);
  const box = { fontFamily: MONO, fontSize: 13, lineHeight: '20px', padding: '7px 12px', whiteSpace: 'pre-wrap', wordBreak: 'break-word', boxSizing: 'border-box', width: '100%', minHeight: 36 };
  const status = problems.length ? null : check === 'checking' ? <span style={S.tiny}>asking the engine…</span>
    : check && check.count != null ? <span style={{ ...S.tiny, color: ui.green, fontWeight: 700 }}>✓ {check.count.toLocaleString()} {countWords(check.count)}</span>
      : check && check.error ? <span style={{ ...S.tiny, color: ui.red, fontWeight: 700 }}>✗ {check.error}{check.pos != null ? ` (at ${check.pos + 1})` : ''}</span> : null;
  const KIND_COLOR = { field: ui.blue, operator: ui.purple, value: ui.green, function: ui.teal, keyword: ui.purple, recent: ui.muted };
  const canApply = !problems.length && !disabled && !!value.trim();
  const pullNote = pull && pull.ok && value.trim() ? `"${pull.ok}" pulled in — edit it, then Enter or Apply` : null;
  const bad = shown.length || (check && check.error);
  const pill = (on) => ({ ...(S.pill ? S.pill(on) : {}), padding: '8px 14px', flex: '0 0 auto', fontFamily: FONT });

  return (
    <div style={{ position: 'relative', fontFamily: FONT, minWidth: 0 }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
        <div style={{ position: 'relative', flex: '1 1 auto', minWidth: 0, border: `1px solid ${bad ? ui.red : focus ? ui.primary : (applied ? ui.primary : ui.border)}`, borderRadius: 8, background: ui.card, boxShadow: focus ? `0 0 0 3px ${ui.primary}18` : 'none' }}>
          <div aria-hidden style={{ ...box, color: ui.fg, pointerEvents: 'none' }}>
            {tokens.length ? tokens.map((t, i) => <span key={i} style={{ color: colorOf(t, i), fontWeight: t.type === 'kw' ? 700 : 400, textDecoration: inError(t) ? `underline wavy ${ui.red}` : 'none', textUnderlineOffset: 3 }}>{t.type === 'string' ? t.raw : t.value}</span>) : <span style={{ color: ui.faint }}>{placeholder}</span>}
            {'​'}
          </div>
          <textarea ref={taRef} value={value} spellCheck={false} autoComplete="off" rows={1} aria-label="ZQL query"
            onChange={(e) => { onChange(e.target.value); setCaret(e.target.selectionStart); if (pull && pull !== 'busy' && pull.error) setPull(null); }}
            onSelect={(e) => setCaret(e.target.selectionStart)} onKeyUp={(e) => setCaret(e.target.selectionStart)} onClick={(e) => setCaret(e.target.selectionStart)}
            onFocus={(e) => { setFocus(true); setCaret(e.target.selectionStart); }} onBlur={() => setTimeout(() => setFocus(false), 150)} onKeyDown={onKey}
            style={{ ...box, position: 'absolute', inset: 0, height: '100%', resize: 'none', border: 'none', outline: 'none', background: 'transparent', color: 'transparent', caretColor: ui.fg, overflow: 'hidden' }} />
          {focus && suggestions.length > 0 ? (
            <div style={{ position: 'absolute', left: 0, right: 0, top: '100%', marginTop: 4, zIndex: 60, background: ui.card, border: `1px solid ${ui.border}`, borderRadius: 10, boxShadow: ui.shadowMd, overflow: 'hidden', maxWidth: 560 }}>
              <div style={{ ...S.tiny, padding: '6px 12px 2px', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 700 }}>
                {suggestions[0] && suggestions[0].kind === 'recent' ? 'Recent queries' : at.state === 'field' ? 'Fields' : at.state === 'op' ? `Operators for ${at.field}` : at.state === 'value' || at.state === 'listValue' ? `Values of ${at.field}` : at.state === 'orderField' ? 'Sort by' : 'Next'} · ↑↓ then Tab or Enter
              </div>
              <div style={{ maxHeight: 300, overflowY: 'auto', padding: 4 }}>
                {suggestions.map((s, i) => (
                  <div key={`${s.kind}:${s.v}`} onMouseDown={(e) => { e.preventDefault(); pick(s); }} onMouseEnter={() => setSel(i)}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', borderRadius: 7, cursor: 'pointer', fontSize: 13, background: i === sel ? `${ui.primary}14` : 'transparent' }}>
                    <span style={{ fontFamily: MONO, fontWeight: 700, color: KIND_COLOR[s.kind] || ui.fg, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: s.kind === 'recent' ? '100%' : 260 }}>{s.v}</span>
                    {s.d && s.kind !== 'recent' ? <span style={{ ...S.tiny, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.d}</span> : null}
                    <span style={{ ...S.tiny, marginLeft: 'auto', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.04em', flex: '0 0 auto' }}>{s.kind}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
        {value || applied ? (
          <button type="button" onClick={() => { onChange(''); setPull(null); if (onClear) onClear(); else onApply(''); }} title="Clear the query"
            style={{ flex: '0 0 auto', border: 'none', background: 'transparent', cursor: 'pointer', color: ui.muted, fontSize: 18, lineHeight: 1, padding: '8px 4px', fontFamily: FONT }}>×</button>
        ) : null}
        <button type="button" onClick={apply} disabled={!canApply}
          title={problems.length ? 'Fix the query first — the problem is named under the box' : 'Run the query (Enter)'}
          style={{ ...pill(true), background: canApply ? ui.primary : ui.surface, color: canApply ? ui.primaryFg : ui.muted, borderColor: canApply ? ui.primary : ui.border, cursor: canApply ? 'pointer' : 'default' }}>Apply</button>
        {savedFilters ? (
          <span ref={filtersRef} style={{ position: 'relative', flex: '0 0 auto' }}>
            <button type="button" onClick={openFilters} title="Search the saved filters and pull one into the box for editing"
              style={{ ...pill(false), display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              {pull === 'busy' ? 'Reading…' : 'Saved filters'} <span style={{ fontSize: 10, color: ui.muted }}>▾</span>
            </button>
            {filtersOpen ? (
              <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4, zIndex: 60, width: 360, maxWidth: '90vw', background: ui.card, border: `1px solid ${ui.border}`, borderRadius: 10, boxShadow: ui.shadowMd, padding: 6 }}>
                <input autoFocus value={filterQ} onChange={(e) => setFilterQ(e.target.value)} placeholder="Find a saved filter…" aria-label="Find a saved filter"
                  onKeyDown={(e) => { if (e.key === 'Enter' && shownFilters[0]) pullFilter(shownFilters[0]); }}
                  style={{ ...S.input, width: '100%', boxSizing: 'border-box', padding: '6px 9px', fontSize: 12.5, marginBottom: 4 }} />
                <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                  {filters === null ? <div style={{ ...S.tiny, padding: 8 }}>reading the saved filters…</div> : null}
                  {filters && filters.error ? <div style={{ ...S.tiny, padding: 8, color: ui.red }}>⚠ {filters.error}</div> : null}
                  {Array.isArray(filters) && !shownFilters.length ? <div style={{ ...S.tiny, padding: 8 }}>{filters.length ? 'No saved filter matches.' : 'No saved filters yet.'}</div> : null}
                  {shownFilters.map((f) => (
                    <div key={f.id} role="button" tabIndex={0} onClick={() => pullFilter(f)} onKeyDown={(e) => { if (e.key === 'Enter') pullFilter(f); }}
                      title={f.description || f.name}
                      style={{ padding: '6px 8px', borderRadius: 7, cursor: 'pointer', fontSize: 13 }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = `${ui.primary}10`; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                      <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.starred ? '★ ' : ''}{f.name}</div>
                      {f.owner || f.description ? <div style={{ ...S.tiny, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{[f.owner, f.description].filter(Boolean).join(' · ')}</div> : null}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </span>
        ) : null}
      </div>
      {(shown.length > 0 || status || hint || pullNote || (pull && pull.error)) ? (
        <div style={{ marginTop: 5, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {shown.slice(0, 3).map((p, i) => (
            <div key={i} style={{ ...S.tiny, color: ui.red, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 700 }}>✗ {p.message}</span>
              {p.fix ? <button type="button" onClick={() => setText(p.fix.apply(value))} style={{ ...(S.pill ? S.pill(false) : {}), padding: '1px 9px', fontSize: 11.5 }}>{p.fix.label}</button> : null}
            </div>
          ))}
          {pull && pull.error ? <span style={{ ...S.tiny, color: ui.red, fontWeight: 700 }}>⚠ {pull.error}</span> : null}
          {status}
          {pullNote ? <span style={S.tiny}>{pullNote}</span> : null}
          {!shown.length && !pullNote && hint ? <span style={S.tiny}>{hint}</span> : null}
        </div>
      ) : null}
    </div>
  );
}
